import { useEffect, useRef, type ReactNode } from 'react'
import { Page } from '@/components/layout/Page'
import { FullPageSpinner } from '@/components/common/Spinner'
import { useSession } from './useSession'

/**
 * Gatekeeper for authenticated routes.
 *
 * Behaviour:
 *   - While the userManager is hydrating (initial boot, silent renew in
 *     flight), render a spinner inside the page chrome.
 *   - Once hydrated and unauthenticated → fire `signinRedirect()` once to
 *     bounce the user to Passport's authorise endpoint. We guard with a
 *     ref so React 18 StrictMode's double-invoked effect doesn't schedule
 *     two redirects and so StrictMode's re-mount doesn't retry mid-flight.
 *   - Once authenticated → render children.
 *
 * Note: `/signed-out` is NOT a step in this flow. It is only reached via
 * Passport's `post_logout_redirect_uri` (i.e. the user explicitly signed
 * out a moment ago). Plain unauthenticated visits to `/` go directly to
 * Passport.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, signin } = useSession()
  const redirectedRef = useRef(false)

  useEffect(() => {
    if (isLoading || isAuthenticated) return
    if (redirectedRef.current) return
    redirectedRef.current = true
    void signin()
  }, [isLoading, isAuthenticated, signin])

  if (isAuthenticated) {
    return <>{children}</>
  }

  return (
    <Page>
      <FullPageSpinner
        label={isLoading ? 'Loading your session' : 'Redirecting to sign in'}
      />
    </Page>
  )
}
